import { useEffect, useRef } from 'react'

import { useStateWithReset } from './use-state-with-reset'
import { PAGE_SIZES, PageSizeType, SetStateFn } from '~config/constants'

export type PageControlsType = {
    page: number
    setPage: SetStateFn<number>
    pageSize: PageSizeType
    setPageSize: SetStateFn<PageSizeType>
    handleReset?: () => void
}

export function usePageControls(
    initialPageSize?: PageSizeType,
    /**
     * When this value changes, page resets to 0. Pass a serialized snapshot
     * of filters that change the result set (search, status, etc). The
     * initial mount is ignored so restored page state is kept.
     */
    resetPageOn?: unknown,
): PageControlsType {
    const [page, setPage, resetPage] = useStateWithReset<number>(0)
    const [pageSize, setPageSize, resetPageSize] =
        useStateWithReset<PageSizeType>(initialPageSize ?? PAGE_SIZES[0])

    function handleReset() {
        resetPage()
        resetPageSize()
    }

    const isFirstResetCheck = useRef(true)
    useEffect(() => {
        if (resetPageOn === undefined) {
            return
        }
        if (isFirstResetCheck.current) {
            isFirstResetCheck.current = false
            return
        }
        setPage(0)
    }, [resetPageOn, setPage])

    return {
        page,
        setPage,
        pageSize,
        setPageSize,
        handleReset,
    }
}
